import type { IPassengerInfo, IRideDto } from "../../dto";
import {
  CourseRouteTypeEnum,
  CourseSettingsSourceEnum,
  DirectionEnum,
  FixCourseMergingMethodEnum,
  FixCourseRebuildRideOptionEnum,
  RideSoftValidationErrorType,
} from "../../enums";
import {
  areDatesWithinRange,
  isDateTimeInclusiveBetween,
  isValidDateTime,
} from "../date.helpers";
import { getRealRouteStationIds } from "../routing-waypoint/routing-waypoint.helpers";
import { calculatePassengersInfoSeatsCount } from "./rides.helpers";

const SHUTTLE_SHIFT_TIME_GAP_FALLBACK_MINUTES = 15;

export const getShuttleShiftGapMinutes = ({
  direction,
  geoProdMaxShiftGap,
  geoProdMaxShiftGapB,
}: {
  direction?: DirectionEnum;
  geoProdMaxShiftGap?: number;
  geoProdMaxShiftGapB?: number;
}): number => {
  if (direction === DirectionEnum.DropOff) {
    return geoProdMaxShiftGapB || SHUTTLE_SHIFT_TIME_GAP_FALLBACK_MINUTES;
  }

  return geoProdMaxShiftGap || SHUTTLE_SHIFT_TIME_GAP_FALLBACK_MINUTES;
};

export const getRideRouteType = (
  rideDto: Partial<IRideDto> | undefined,
): CourseRouteTypeEnum | undefined => {
  if (!rideDto) {
    return undefined;
  }

  return rideDto?.metadata?.courseType || rideDto?.courseRouteType;
};

export const isShuttleRideDto = (
  rideDto: Partial<IRideDto> | undefined,
): boolean => getRideRouteType(rideDto) === CourseRouteTypeEnum.Shuttle;

export const isNeverMergeFlexRideDto = (
  rideDto: Partial<IRideDto> | undefined,
): boolean =>
  getRideRouteType(rideDto) === CourseRouteTypeEnum.Flex &&
  (rideDto?.metadata?.mergingMethod === FixCourseMergingMethodEnum.NeverMerge ||
    rideDto?.courseMergingMethod === FixCourseMergingMethodEnum.NeverMerge);

export const isAllowMergingFlexRideDto = (
  rideDto: Partial<IRideDto> | undefined,
): boolean =>
  getRideRouteType(rideDto) === CourseRouteTypeEnum.Flex &&
  !isNeverMergeFlexRideDto(rideDto);

export const isLockedFixedRouteDropTargetDto = (
  rideDto: Partial<IRideDto> | undefined,
): boolean => isShuttleRideDto(rideDto) || isNeverMergeFlexRideDto(rideDto);

/** Shuttle and NeverMerge are cut by the drop ride window, not the drag-offset window. Allow-merge keeps the drag window — convert can still expand station clocks. */
export const shouldSkipRideDropWindowOffsetCheck = (
  dropRideDto: Partial<IRideDto> | undefined,
): boolean =>
  isShuttleRideDto(dropRideDto) || isNeverMergeFlexRideDto(dropRideDto);

const resolveRideCourseSettingsSource = (
  rideDto: Partial<IRideDto> | undefined,
): CourseSettingsSourceEnum | undefined =>
  rideDto?.courseSettingsSource ?? rideDto?.metadata?.settingsSource;

/** Mirrors BE isCourseManualPricingMode for shuttle merge pre-checks. */
export const isCourseManualPricingModeDto = (
  rideDto: Partial<IRideDto> | undefined,
): boolean => {
  if (!rideDto?.courseId) {
    return false;
  }

  const settingsSource = resolveRideCourseSettingsSource(rideDto);
  if (settingsSource === undefined) {
    return false;
  }

  return settingsSource !== CourseSettingsSourceEnum.PriceRates;
};

/**
 * Manual shuttle merge: use metadata.maxPassengers when the ride already has a vehicle type.
 * BE syncs metadata.maxPassengers from vehicleType.maxPassengers on course produce
 * (handleFixedRidePriceRate / resolveManualCourseVehicleContext) and on ride save
 * (RidesService.resolveMaxPassengers). Price-rates shuttles skip FE capacity — BE rematches rate.
 */
export const resolveShuttleMergeEffectiveMaxPassengers = (
  dropRideDto: Partial<IRideDto>,
): number | undefined => {
  if (
    !isShuttleRideDto(dropRideDto) ||
    !isCourseManualPricingModeDto(dropRideDto) ||
    !dropRideDto.vehicleTypeId
  ) {
    return undefined;
  }

  const maxPassengers = dropRideDto.metadata?.maxPassengers;
  if (typeof maxPassengers === "number" && maxPassengers > 0) {
    return maxPassengers;
  }

  return undefined;
};

export const areMergedPassengersWithinShuttleCapacity = ({
  dropRideDto,
  passengers,
}: {
  dropRideDto: Partial<IRideDto>;
  passengers: { seats?: number }[];
}): boolean => {
  const effectiveMaxPassengers =
    resolveShuttleMergeEffectiveMaxPassengers(dropRideDto);
  if (effectiveMaxPassengers === undefined) {
    return true;
  }

  const currentSeatsCount = dropRideDto.metadata?.seatsCount ?? 0;
  const addedSeatsCount = calculatePassengersInfoSeatsCount(passengers);
  const projectedSeatsCount = currentSeatsCount + addedSeatsCount;

  return projectedSeatsCount <= effectiveMaxPassengers;
};

export const getCurrentRideStationIds = (
  rideDto: Partial<IRideDto> | undefined,
): number[] => {
  const realRouteIds = getRealRouteStationIds(rideDto?.metadata?.stationsInfo);
  return realRouteIds.length
    ? realRouteIds
    : rideDto?.metadata?.stationIds || [];
};

export const arePassengersCompatibleWithShuttleStations = ({
  rideDto,
  passengers,
}: {
  rideDto: Partial<IRideDto>;
  passengers: Pick<IPassengerInfo, "from" | "to">[];
}): boolean => {
  if (!passengers?.length) {
    return true;
  }

  return passengersStationsContain(
    rideDto.metadata?.stationIds || [],
    passengers,
  );
};

const resolveShuttleShiftAnchorStationId = ({
  direction,
  fromStationId,
  toStationId,
}: {
  direction?: DirectionEnum;
  fromStationId?: number;
  toStationId?: number;
}): number | undefined => {
  if (direction === DirectionEnum.DropOff) {
    return fromStationId;
  }

  return toStationId;
};

const resolvePassengerTimeForShuttleMove = ({
  passenger,
  sourceRideDto,
}: {
  passenger: Pick<IPassengerInfo, "from"> &
    Partial<Pick<IPassengerInfo, "id" | "shiftHour">>;
  sourceRideDto?: Partial<IRideDto>;
}): TDateISO | undefined => {
  if (passenger.shiftHour && isValidDateTime(passenger.shiftHour)) {
    return passenger.shiftHour;
  }

  const passengerInfo = sourceRideDto?.metadata?.passengersInfo?.find(
    info => info.id === passenger.id,
  );
  if (passengerInfo?.shiftHour && isValidDateTime(passengerInfo.shiftHour)) {
    return passengerInfo.shiftHour;
  }

  const fromStationId = passengerInfo?.from ?? passenger.from;
  const sourceArrivalTime = sourceRideDto?.metadata?.stationsInfo?.find(
    station => station.id === fromStationId,
  )?.arrivalTime;
  if (sourceArrivalTime && isValidDateTime(sourceArrivalTime)) {
    return sourceArrivalTime;
  }

  return undefined;
};

/** Passenger shift time must stay within geoProdMaxShiftGap of the target shift-anchor station. */
export const arePassengersTimesWithinShuttleShiftGap = ({
  rideDto,
  sourceRideDto,
  passengers,
  shiftGapMinutes,
}: {
  rideDto: Partial<IRideDto>;
  sourceRideDto?: Partial<IRideDto>;
  passengers: (Pick<IPassengerInfo, "from" | "to"> &
    Partial<Pick<IPassengerInfo, "id" | "shiftHour">>)[];
  shiftGapMinutes: number;
}): boolean => {
  if (!passengers?.length) {
    return true;
  }

  const stationsInfo = rideDto.metadata?.stationsInfo;

  for (const passenger of passengers) {
    const passengerTime = resolvePassengerTimeForShuttleMove({
      passenger,
      sourceRideDto,
    });
    if (!passengerTime) {
      continue;
    }

    const anchorStationId = resolveShuttleShiftAnchorStationId({
      direction: rideDto.direction,
      fromStationId: passenger.from,
      toStationId: passenger.to,
    });
    const anchorStation = stationsInfo?.find(
      station => station.id === anchorStationId,
    );
    if (!anchorStation) {
      continue;
    }

    const stationArrivalTime = anchorStation.arrivalTime;
    if (!stationArrivalTime || !isValidDateTime(stationArrivalTime)) {
      return false;
    }

    if (
      !areDatesWithinRange(passengerTime, stationArrivalTime, shiftGapMinutes)
    ) {
      return false;
    }
  }

  return true;
};

type TDragPassengerStationsAndTime = Pick<IPassengerInfo, "from" | "to"> &
  Partial<Pick<IPassengerInfo, "id" | "shiftHour" | "seats">>;

/** NeverMerge-flex FE pre-check: passenger shift (or source arrival) must sit inside the drop ride window. */
export const arePassengersTimesWithinRideBounds = ({
  rideDto,
  sourceRideDto,
  passengers,
}: {
  rideDto: Partial<IRideDto>;
  sourceRideDto?: Partial<IRideDto>;
  passengers: TDragPassengerStationsAndTime[];
}): boolean => {
  if (!passengers?.length) {
    return true;
  }

  const { rideStart, rideEnd } = rideDto;
  if (
    !rideStart ||
    !rideEnd ||
    !isValidDateTime(rideStart) ||
    !isValidDateTime(rideEnd)
  ) {
    return true;
  }

  for (const passenger of passengers) {
    const passengerTime = resolvePassengerTimeForShuttleMove({
      passenger,
      sourceRideDto,
    });
    if (!passengerTime) {
      continue;
    }

    if (
      !isDateTimeInclusiveBetween({
        value: passengerTime,
        start: rideStart,
        end: rideEnd,
      })
    ) {
      return false;
    }
  }

  return true;
};

/** Locked drop FE pre-check: stations, order, manual shuttle capacity, and the drop ride window. Site shift gap stays on BE. */
export const areLockedFixedRouteDropPassengersCompatible = ({
  dropRideDto,
  sourceRideDto,
  passengers,
}: {
  dropRideDto: Partial<IRideDto>;
  sourceRideDto?: Partial<IRideDto>;
  passengers: TDragPassengerStationsAndTime[];
}): boolean => {
  if (!isLockedFixedRouteDropTargetDto(dropRideDto)) {
    return true;
  }

  // Same ordered real stops as BE getRideRealStationIdsOrdered (waypoints excluded).
  const dropStationRideDto = {
    ...dropRideDto,
    metadata: {
      ...dropRideDto.metadata,
      stationIds: getCurrentRideStationIds(dropRideDto),
    },
  };

  if (
    !arePassengersCompatibleWithShuttleStations({
      rideDto: dropStationRideDto,
      passengers,
    })
  ) {
    return false;
  }

  if (
    isShuttleRideDto(dropRideDto) &&
    !areMergedPassengersWithinShuttleCapacity({ dropRideDto, passengers })
  ) {
    return false;
  }

  // Coarse window only. Convert cannot rebuild a shuttle or a NeverMerge flex.
  return arePassengersTimesWithinRideBounds({
    rideDto: dropRideDto,
    sourceRideDto,
    passengers,
  });
};

/** Allow-merge: new stops or wrong order can be fixed by converting to dynamic, so ask on the client before the move. */
export const allowMergeFlexDropRequiresConvertForStations = ({
  dropRideDto,
  passengers,
}: {
  dropRideDto: Partial<IRideDto>;
  passengers: Pick<IPassengerInfo, "from" | "to">[] | undefined;
}): boolean => {
  if (!isAllowMergingFlexRideDto(dropRideDto) || !passengers?.length) {
    return false;
  }

  return !passengersStationsContain(
    getCurrentRideStationIds(dropRideDto),
    passengers,
  );
};

const getRideRealStationIdsSet = (
  rideDto: Partial<IRideDto> | undefined,
): Set<number> => {
  const realRouteIds = getRealRouteStationIds(rideDto?.metadata?.stationsInfo);
  const ids = realRouteIds.length
    ? realRouteIds
    : (rideDto?.metadata?.stationIds ?? []);

  return new Set(ids);
};

export const passengersStationsContain = (
  stationIds: number[],
  passengersInfos: Pick<IPassengerInfo, "from" | "to">[] | undefined,
): boolean => {
  if (!passengersInfos?.length) {
    return false;
  }

  if (!stationIds?.length) {
    return false;
  }

  for (const passenger of passengersInfos) {
    const fromStationId = passenger.from ? Number(passenger.from) : null;
    const toStationId = passenger.to ? Number(passenger.to) : null;

    if (!fromStationId || !toStationId) {
      return false;
    }

    const fromIndex = stationIds.indexOf(fromStationId);
    const toIndex = stationIds.indexOf(toStationId);

    if (fromIndex === -1 || toIndex === -1) {
      return false;
    }

    if (fromIndex >= toIndex) {
      return false;
    }
  }

  return true;
};

export const rideShouldCleanupEmptyStations = (
  rideDto: Partial<IRideDto>,
  isRideWithEmptyStationsAllowedByRideType?: boolean,
): boolean => {
  /* ride dto value for saved entity or form value for new not saved ride */
  if (
    rideDto?.isRideWithEmptyStationsAllowed ||
    isRideWithEmptyStationsAllowedByRideType
  ) {
    return false;
  }

  if (!rideDto?.courseId) {
    return true;
  }

  const routeType = getRideRouteType(rideDto);

  if (routeType === CourseRouteTypeEnum.Shuttle) {
    return false;
  }

  // KeepEmptyStations stays shuttle-like. Other flex options still return false here:
  // empty-stop drop is a backend rebuild, not a form-side stations cleanup.
  if (
    routeType === CourseRouteTypeEnum.Flex &&
    rideDto.courseRebuildRideOption ===
      FixCourseRebuildRideOptionEnum.KeepEmptyStations
  ) {
    return false;
  }

  /* fallback default for fixed rides, may be changed while adding ride types */
  return false;
};

export const rideConvertToDynamic = (
  rideDto: Partial<IRideDto> | undefined,
): Partial<IRideDto> | undefined => {
  if (!rideDto) {
    return undefined;
  }

  const {
    courseId: _courseId,
    courseRebuildRideOption: _courseRebuildRideOption,
    courseName: _courseName,
    courseSettingsSource: _courseSettingsSource,
    courseRouteType: _courseRouteType,
    courseMergingMethod: _courseMergingMethod,
    courseStationsIds: _courseStationsIds,
    metadata,
    ...clonedRide
  } = rideDto;

  const {
    courseType: _courseType,
    mergingMethod: _mergingMethod,
    rebuildRideOption: _rebuildRideOption,
    rebuildRouteType: _rebuildRouteType,
    settingsSource: _settingsSource,
    ...cleanedMetadata
  } = metadata || {};

  return {
    ...clonedRide,
    courseId: null,
    metadata: cleanedMetadata,
  };
};

export const rideShouldOptimizeOnRebuild = (
  rideDto: Partial<IRideDto>,
): boolean => {
  if (!rideDto?.courseId) {
    return true;
  }

  const routeType = getRideRouteType(rideDto);

  if (routeType === CourseRouteTypeEnum.Shuttle) {
    return false;
  }

  // KeepEmptyStations stays shuttle-like. Other flex rebuilds still return false here:
  // OSRM optimize vs recast is decided on BE shouldOptimizeRouteOnRideRecalculate.
  if (
    routeType === CourseRouteTypeEnum.Flex &&
    rideDto.courseRebuildRideOption ===
      FixCourseRebuildRideOptionEnum.KeepEmptyStations
  ) {
    return false;
  }

  /* fallback default for fixed rides, may be changed while adding ride types */
  return false;
};

export const rideShouldBeConvertedToDynamic = (
  rideDto: Partial<IRideDto>,
): boolean => {
  if (!rideDto?.courseId) {
    return false;
  }

  const routeType = getRideRouteType(rideDto);

  if (routeType === CourseRouteTypeEnum.Shuttle) {
    return false;
  }

  // Same as BE isAllowMergingFlexRide: flex that is not NeverMerge (missing method counts as allow).
  if (isAllowMergingFlexRideDto(rideDto)) {
    return true;
  }

  /* fallback default for fixed rides, may be changed while adding ride types */
  return false;
};

export const passengerAddIntroducesNewStationsToRide = ({
  rideDto,
  stationFromId,
  stationToId,
}: {
  rideDto: Partial<IRideDto>;
  stationFromId: number | undefined;
  stationToId: number | undefined;
}): boolean => {
  const fromId = stationFromId ? Number(stationFromId) : Number.NaN;
  const toId = stationToId ? Number(stationToId) : Number.NaN;

  if (!Number.isFinite(fromId) || !Number.isFinite(toId)) {
    return false;
  }

  const rideStationIds = getRideRealStationIdsSet(rideDto);

  // Real passenger stops only (routing map waypoints excluded).
  return !rideStationIds.has(fromId) || !rideStationIds.has(toId);
};

/** New stops or wrong pickup→dropoff order on the fixed route — convert is the only fix on the client. */
export const passengerAddRequiresConvertForStationRoute = ({
  rideDto,
  stationFromId,
  stationToId,
}: {
  rideDto: Partial<IRideDto>;
  stationFromId: number | undefined;
  stationToId: number | undefined;
}): boolean => {
  if (
    passengerAddIntroducesNewStationsToRide({
      rideDto,
      stationFromId,
      stationToId,
    })
  ) {
    return true;
  }

  const fromId = stationFromId ? Number(stationFromId) : Number.NaN;
  const toId = stationToId ? Number(stationToId) : Number.NaN;
  if (!Number.isFinite(fromId) || !Number.isFinite(toId)) {
    return false;
  }

  const orderedStationIds = getCurrentRideStationIds(rideDto);
  return !passengersStationsContain(orderedStationIds, [
    { from: fromId, to: toId },
  ]);
};

export const shouldConfirmConvertToDynamicOnPassengerAdd = ({
  rideDto,
  stationFromId,
  stationToId,
}: {
  rideDto: Partial<IRideDto>;
  stationFromId: number | undefined;
  stationToId: number | undefined;
}): boolean => {
  if (!rideShouldBeConvertedToDynamic(rideDto)) {
    return false;
  }

  // Station route on the client; site-scoped shift gap is confirmed by backend soft validation.
  return passengerAddRequiresConvertForStationRoute({
    rideDto,
    stationFromId,
    stationToId,
  });
};

export const rideSoftValidationRequiresConvertToDynamic = (
  validationErrors?: { type?: RideSoftValidationErrorType }[],
): boolean =>
  Boolean(
    validationErrors?.some(
      error =>
        error.type ===
          RideSoftValidationErrorType.FlexRideWillBeMergedToDynamic ||
        error.type ===
          RideSoftValidationErrorType.FlexRideWillBeMergedToDynamicStationMismatch ||
        error.type ===
          RideSoftValidationErrorType.FlexRideWillBeMergedToDynamicTimeMismatch,
    ),
  );
